import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Card, CardHeader, DatePicker, ErrorState, Input, PageHeader, PageLoader, Select, Textarea, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { LineItemsEditor, lineError, toApiLine, toEditLine, EditLine } from '../../../components/common/LineItemsEditor/LineItemsEditor';
import { CustomerPicker, WorkerMultiSelect } from '../../../components/common/Pickers/Pickers';
import { FormSection, KeyValues, TableSection, TwoColumn } from '../../../components/common/Parts/Parts';
import { useFetch } from '../../../hooks/useFetch';
import { PRIORITY_OPTIONS } from '../../../constants/status';
import { withHomeState } from '../../../constants/location';
import { masterOptions } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { money } from '../../../utils/currency';
import { toISODate } from '../../../utils/date';
import { computeTotals } from '../../../lib/pricing';
import { jobService } from '../services/jobService';
import { customerService } from '../../customers/services/customerService';
import type { Priority } from '../types/jobTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';

const EMPTY = {
  customerId: null as string | null, customerName: '', serviceType: 'repair', title: '', description: '', siteLine: '', siteCity: '', sitePincode: '',
  scheduledDate: toISODate(new Date()), priority: 'normal' as Priority, assignedTo: [] as string[],
};

export default function JobForm() {
  const { id = '' } = useParams();
  const editing = !!id;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { form, setForm, set, errors, setErrors } = useForm(EMPTY);
  const [labour, setLabour] = useState<EditLine[]>([]);
  const [materials, setMaterials] = useState<EditLine[]>([]);
  const [saving, setSaving] = useState(false);
  useMasters();

  const { data: job, loading, error, reload } = useFetch(
    () => (editing ? jobService.get(id) : Promise.resolve(null)),
    [id],
  );
  useCrumb(`/jobs/${id}`, job?.serviceNo);

  useEffect(() => {
    if (!job) return;
    setForm({
      ...EMPTY,
      customerId: job.customerId, customerName: job.customer?.name ?? '', serviceType: job.serviceType ?? 'repair', title: job.title ?? '',
      description: job.description ?? '', siteLine: job.siteAddress?.line ?? '', siteCity: job.siteAddress?.city ?? '', sitePincode: job.siteAddress?.pincode ?? '',
      scheduledDate: job.scheduledDate ? toISODate(new Date(job.scheduledDate)) : '', priority: job.priority ?? 'normal',
    });
    setLabour(job.labourItems.map((l) => toEditLine(l, 'description')));
  }, [job, setForm]);

  useEffect(() => {
    const cid = params.get('customerId');
    if (!editing && cid) customerService.get(cid).then((c) => pickCustomer(cid, c)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pickCustomer(cid, c) {
    const a = c?.siteAddresses?.[0] ?? c?.billingAddress ?? {};
    setForm((f) => ({ ...f, customerId: cid, customerName: c?.name ?? '', siteLine: f.siteLine || a.line || '', siteCity: f.siteCity || a.city || '', sitePincode: f.sitePincode || a.pincode || '' }));
    setErrors((e) => ({ ...e, customerId: undefined }));
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!form.customerId) errs.customerId = 'Pick the customer';
    const lineMsg = lineError(labour) || lineError(materials);
    if (lineMsg) errs.lines = lineMsg;
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error(errs.lines || 'Please fix the highlighted fields');

    const common = {
      serviceType: form.serviceType,
      title: trimOrUndef(form.title),
      description: trimOrUndef(form.description),
      siteAddress: withHomeState({ line: form.siteLine.trim(), city: form.siteCity.trim(), pincode: form.sitePincode.trim() }),
      scheduledDate: form.scheduledDate || undefined,
      priority: form.priority,
      labourItems: labour.map((l) => toApiLine(l, 'description')),
    };
    setSaving(true);
    try {
      const data = id
        ? await jobService.update(id, common)
        : await jobService.create({ ...common, customerId: form.customerId as string, assignedTo: form.assignedTo, materials: materials.map((m) => toApiLine(m, 'name', { supplyBy: true })) });
      toast.success(editing ? 'Job updated' : `Job ${data.serviceNo} created`);
      navigate(`/jobs/${data._id}`, { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (editing && loading && !job) return <PageLoader />;
  if (job && ['invoiced', 'cancelled'].includes(job.status)) return <ErrorState error={{ message: `This job is ${job.status} and cannot be changed.` }} />;

  const totals = computeTotals([...labour, ...materials.filter((m) => m.supplyBy === 'us')].map((l) => ({ ...l, qty: Number(l.qty) })), true);
  const actions = (
    <>
      <Button variant="outline" onClick={() => navigate(editing ? `/jobs/${id}` : '/jobs')} disabled={saving}>Cancel</Button>
      <Button type="submit" form="job-form" leftIcon="check" loading={saving}>{editing ? 'Save changes' : 'Create job'}</Button>
    </>
  );

  return (
    <>
      <PageHeader title={editing ? `Edit ${job?.serviceNo ?? 'job'}` : 'New service job'} subtitle={editing ? job?.customer?.name : 'For repairs and small work. Big work usually starts from a quotation.'} actions={actions} />
      <form id="job-form" onSubmit={submit} noValidate>
        <TwoColumn
          main={
            <>
              <FormSection title="Customer and work">
                {editing ? (
                  <Input label="Customer" value={form.customerName} disabled containerClassName="sm:col-span-2" hint="Customer cannot change on an existing job" />
                ) : (
                  <CustomerPicker required value={form.customerId} selectedLabel={form.customerName} onChange={pickCustomer} error={errors.customerId} containerClassName="sm:col-span-2" />
                )}
                <Input label="Job title" value={form.title} onChange={set('title')} placeholder="e.g. Kitchen MCB tripping" />
                <Select label="Type of work" options={masterOptions('serviceType', { keep: form.serviceType })} value={form.serviceType} onChange={set('serviceType')} searchable={false} />
                <Textarea label="Problem / work details" rows={3} value={form.description} onChange={set('description')} maxLength={3000} containerClassName="sm:col-span-2" />
              </FormSection>

              <FormSection title="Visit">
                <DatePicker label="Visit date" value={form.scheduledDate} onChange={set('scheduledDate')} />
                <Select label="Priority" options={PRIORITY_OPTIONS} value={form.priority} onChange={set('priority')} searchable={false} />
                {!editing && <WorkerMultiSelect value={form.assignedTo} onChange={set('assignedTo')} containerClassName="sm:col-span-2" hint="Leave empty to assign later" />}
                <Textarea label="Site address" rows={2} value={form.siteLine} onChange={set('siteLine')} containerClassName="sm:col-span-2" />
                <Input label="City" value={form.siteCity} onChange={set('siteCity')} />
                <Input label="Pincode" inputMode="numeric" maxLength={6} value={form.sitePincode} onChange={set('sitePincode')} />
              </FormSection>

              <TableSection title="Labour" subtitle="Work to be billed">
                <LineItemsEditor lines={labour} onChange={setLabour} itemType="service" textLabel="Work" addLabel="Add work" emptyText="No labour yet. You can add it later too." />
              </TableSection>

              {!editing && (
                <TableSection title="Materials" subtitle="Planned material. Issue it from stock on the job page.">
                  <LineItemsEditor lines={materials} onChange={setMaterials} itemType="material" textLabel="Material" supplyBy addLabel="Add material" emptyText="No materials planned." />
                </TableSection>
              )}
              <div className="flex justify-end gap-2">{actions}</div>
            </>
          }
          aside={
            <Card>
              <CardHeader title="Estimate" />
              <div className="p-5 pt-4">
                <KeyValues rows={[['Labour lines', labour.length], ['Material lines', materials.length], ['Estimated bill (with GST)', money(totals.grandTotal), { strong: true }]]} />
                <p className="mt-3 text-xs text-ink-400">The final invoice uses labour plus the material actually issued to the job.</p>
              </div>
            </Card>
          }
        />
      </form>
    </>
  );
}
